import { useQuery } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex, Flex } from '@/components/base';
import GridTopList from '@/components/GridList/GridTopList';
import { MainText } from '@/components/Typography/NamedTypo';
import queryKeys from '@/configs/react-query-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { GPT_DOMAIN } from '@/types/gptConst';

import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';
type Props = {
  title: string;
  field: string;
};

const { ContentBox, ContentHeader, DownloadButton } = SearchContentStyles;

const FeedbackAttrTopListContainer = (props: Props) => {
  const filterMap = useStructuredFilterMap();
  const { alias, searchResult, filterQueryFormats } = useSearchDetailContext();
  const { addToFilterSingleValue } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryFn: () =>
      service.globalSearch.fetchFeedbackFieldDist(
        searchResult.alias,
        props.field,
      ),
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT,
      props.field,
      searchResult.alias,
    ],
  });

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    const filterPayload = {
      [props.field]: structuredFilterUtils.buildSingleValue(modalState),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [props.field, modalState],
      options: {
        queryKeys: [props.field, modalState],
        title: 'Feedback list',
        subTitle: `${filterMap[props.field].columnName} : ${modalState}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus?.sortBy,
                order: sortStatus?.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    await addToFilterSingleValue(searchResult, filterQueryFormats, {
      segment: {
        value: modalState,
        name: modalState,
      },
      columnKey: props.field,
    });
  };

  return (
    <ContentBox padding="24px" g={24}>
      <Flex j="space-between" a="center">
        <ContentHeader>{props.title}</ContentHeader>
        <DownloadButton onClick={() => {}} />
      </Flex>
      <ErrorBoundary
        fallback={
          <CenterFlex style={{ height: 250 }}>
            <MainText dim>Error occurred</MainText>
          </CenterFlex>
        }
      >
        <GridTopList
          data={data?.buckets.map((bucket) => {
            return {
              bucket: bucket.bucket,
              count: bucket.count,
            };
          })}
          isLoading={isLoading}
          onClickItem={(e, bucket) => {
            setPos({
              top: e.clientY,
              left: e.clientX,
            });
            setModalState(bucket.bucket.toString());
            open();
          }}
        />
      </ErrorBoundary>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </ContentBox>
  );
};

export default FeedbackAttrTopListContainer;
